import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { toast } from "sonner";
import { 
  Car, 
  ArrowLeft,
  User,
  Phone,
  Mail,
  MapPin,
  Check,
  Loader2,
  AlertCircle,
  Repeat,
  Calendar,
  Gauge,
  DollarSign,
  FileText
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CANADIAN_PROVINCES = [
  "Alberta", "British Columbia", "Manitoba", "New Brunswick", "Newfoundland and Labrador",
  "Northwest Territories", "Nova Scotia", "Nunavut", "Ontario", "Prince Edward Island",
  "Quebec", "Saskatchewan", "Yukon"
];

const VEHICLE_CONDITIONS = [
  "Excellent - Like new, minimal wear",
  "Good - Minor wear, runs well",
  "Fair - Some mechanical or cosmetic issues",
  "Poor - Significant issues, may need repairs"
];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 30 }, (_, i) => currentYear - i);

export const TradeIn = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [errors, setErrors] = useState({});
  const lastSubmitTime = useRef(0);
  
  const [formData, setFormData] = useState({
    // Contact info
    full_name: "",
    phone: "",
    email: "",
    community: "",
    province: "",
    // Vehicle info
    vehicle_year: "",
    vehicle_make: "",
    vehicle_model: "",
    mileage: "",
    condition: "",
    balance_owing: "",
    // Preferences
    vehicle_preference: "",
    notes: "",
    // Consent
    contact_consent: false,
    // Honeypot
    website: ""
  });

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Contact validation
    if (!formData.full_name.trim()) {
      newErrors.full_name = "Name is required";
    }
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    }
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    if (!formData.community.trim()) {
      newErrors.community = "Community is required";
    }
    if (!formData.province) {
      newErrors.province = "Province is required";
    }
    
    // Vehicle validation
    if (!formData.vehicle_year) {
      newErrors.vehicle_year = "Year is required";
    }
    if (!formData.vehicle_make.trim()) {
      newErrors.vehicle_make = "Make is required";
    }
    if (!formData.vehicle_model.trim()) {
      newErrors.vehicle_model = "Model is required";
    }
    if (!formData.mileage.trim()) {
      newErrors.mileage = "Mileage is required";
    } else if (isNaN(formData.mileage) || parseInt(formData.mileage) < 0) {
      newErrors.mileage = "Please enter a valid mileage";
    }
    if (!formData.condition) {
      newErrors.condition = "Condition is required";
    }
    
    // Consent
    if (!formData.contact_consent) {
      newErrors.contact_consent = "You must agree to be contacted";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Rate limiting
    const now = Date.now();
    if (now - lastSubmitTime.current < 5000) {
      toast.error("Please wait a moment before submitting again");
      return;
    }
    
    if (!validateForm()) {
      toast.error("Please fix the errors below");
      const firstErrorField = document.querySelector('.field-error');
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }
    
    // Honeypot check
    if (formData.website) {
      setIsComplete(true);
      return;
    }
    
    setIsSubmitting(true);
    lastSubmitTime.current = now;
    
    try {
      await axios.post(`${API}/trade-ins`, {
        full_name: formData.full_name.trim(),
        phone: formData.phone.trim(),
        email: formData.email.trim(),
        community: formData.community.trim(),
        province: formData.province,
        vehicle_year: parseInt(formData.vehicle_year),
        vehicle_make: formData.vehicle_make.trim(),
        vehicle_model: formData.vehicle_model.trim(),
        mileage: parseInt(formData.mileage),
        condition: formData.condition,
        balance_owing: formData.balance_owing ? parseFloat(formData.balance_owing) : null,
        vehicle_preference: formData.vehicle_preference.trim() || null,
        notes: formData.notes.trim() || null
      });
      
      setIsComplete(true);
      toast.success("Thank you! Your trade-in enquiry has been submitted.");
    } catch (error) {
      console.error("Submission error:", error);
      if (error.response?.status === 429) {
        toast.error("Too many submissions. Please try again later.");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderSuccess = () => (
    <div className="success-container fade-in" data-testid="tradein-success">
      <div className="success-icon">
        <Check strokeWidth={3} />
      </div>
      <h2>Thank You!</h2>
      <p>
        Your trade-in enquiry has been submitted successfully.<br />
        Our team will review your information and contact you soon to discuss your trade-in and financing options.
      </p>
      <div className="info-box" style={{ marginTop: '1.5rem', textAlign: 'left' }}>
        <h3 style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>What happens next?</h3>
        <ul style={{ margin: 0, paddingLeft: '1.25rem', color: 'var(--slate-600)' }}>
          <li>Our team will review your vehicle details</li>
          <li>We may request photos of the exterior, interior, and odometer</li>
          <li>We'll discuss financing options for your next vehicle</li>
        </ul>
      </div>
      <div style={{ marginTop: '2rem' }}>
        <Link to="/" className="btn btn-primary" style={{ textDecoration: 'none' }}>
          Back to Home
        </Link>
      </div>
    </div>
  );

  return (
    <div className="tradein-page">
      <Helmet>
        <title>Trade-In Enquiry | Moto Money Finance</title>
        <meta name="description" content="Have a vehicle to trade in? Submit your trade-in enquiry to Moto Money Finance and let our team help you with financing your next vehicle." />
      </Helmet>

      {/* Header */}
      <header className="site-header">
        <div className="header-container">
          <Link to="/" className="logo" data-testid="logo">
            <Car size={32} />
            <span>Moto Money<strong>Finance</strong></span>
          </Link>
          <nav className="nav-links">
            <Link to="/" className="nav-link">
              <ArrowLeft size={18} />
              Back to Home
            </Link>
          </nav>
        </div>
      </header>

      <div className="tradein-container">
        <div className="tradein-card">
          {isComplete ? renderSuccess() : (
            <>
              <div className="tradein-header">
                <div className="tradein-icon">
                  <Repeat size={32} />
                </div>
                <h1>Trade-In Enquiry</h1>
                <p>
                  Thinking about trading in your current vehicle? Tell us about it and let our team help you explore your options.
                </p>
                <div className="info-note">
                  <AlertCircle size={16} />
                  <span>Note: Our team may request photos of your vehicle (exterior, interior, odometer) after you submit this enquiry to help assess your trade-in.</span>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="tradein-form" data-testid="tradein-form">
                {/* Contact Information */}
                <div className="form-section">
                  <h2>Your Contact Information</h2>
                  
                  <div className="form-group">
                    <label className="form-label">
                      <User size={14} /> Full Name *
                    </label>
                    <input
                      type="text"
                      className={`form-input ${errors.full_name ? 'input-error' : ''}`}
                      placeholder="John Smith"
                      value={formData.full_name}
                      onChange={(e) => updateField('full_name', e.target.value)}
                      data-testid="tradein-name-input"
                    />
                    {errors.full_name && (
                      <span className="field-error" role="alert">
                        <AlertCircle size={14} /> {errors.full_name}
                      </span>
                    )}
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">
                        <Phone size={14} /> Phone *
                      </label>
                      <input
                        type="tel"
                        className={`form-input ${errors.phone ? 'input-error' : ''}`}
                        placeholder="(204) 555-1234"
                        value={formData.phone}
                        onChange={(e) => updateField('phone', e.target.value)}
                        data-testid="tradein-phone-input"
                      />
                      {errors.phone && (
                        <span className="field-error" role="alert">
                          <AlertCircle size={14} /> {errors.phone}
                        </span>
                      )}
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        <Mail size={14} /> Email *
                      </label>
                      <input
                        type="email"
                        className={`form-input ${errors.email ? 'input-error' : ''}`}
                        placeholder="john@email.com"
                        value={formData.email}
                        onChange={(e) => updateField('email', e.target.value)}
                        data-testid="tradein-email-input"
                      />
                      {errors.email && (
                        <span className="field-error" role="alert">
                          <AlertCircle size={14} /> {errors.email}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">
                        <MapPin size={14} /> Community *
                      </label>
                      <input
                        type="text"
                        className={`form-input ${errors.community ? 'input-error' : ''}`}
                        placeholder="Winnipeg"
                        value={formData.community}
                        onChange={(e) => updateField('community', e.target.value)}
                        data-testid="tradein-community-input"
                      />
                      {errors.community && (
                        <span className="field-error" role="alert">
                          <AlertCircle size={14} /> {errors.community}
                        </span>
                      )}
                    </div>
                    <div className="form-group">
                      <label className="form-label">Province *</label>
                      <select
                        className={`form-input form-select ${errors.province ? 'input-error' : ''}`}
                        value={formData.province}
                        onChange={(e) => updateField('province', e.target.value)}
                        data-testid="tradein-province-select"
                      >
                        <option value="">Select Province</option>
                        {CANADIAN_PROVINCES.map(province => (
                          <option key={province} value={province}>{province}</option>
                        ))}
                      </select>
                      {errors.province && (
                        <span className="field-error" role="alert">
                          <AlertCircle size={14} /> {errors.province}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Vehicle Information */}
                <div className="form-section">
                  <h2>Your Current Vehicle</h2>
                  
                  <div className="form-row-3">
                    <div className="form-group">
                      <label className="form-label">
                        <Calendar size={14} /> Year *
                      </label>
                      <select
                        className={`form-input form-select ${errors.vehicle_year ? 'input-error' : ''}`}
                        value={formData.vehicle_year}
                        onChange={(e) => updateField('vehicle_year', e.target.value)}
                        data-testid="tradein-year-select"
                      >
                        <option value="">Year</option>
                        {YEARS.map(year => (
                          <option key={year} value={year}>{year}</option>
                        ))}
                      </select>
                      {errors.vehicle_year && (
                        <span className="field-error" role="alert">
                          <AlertCircle size={14} /> {errors.vehicle_year}
                        </span>
                      )}
                    </div>
                    <div className="form-group">
                      <label className="form-label">Make *</label>
                      <input
                        type="text"
                        className={`form-input ${errors.vehicle_make ? 'input-error' : ''}`}
                        placeholder="Toyota"
                        value={formData.vehicle_make}
                        onChange={(e) => updateField('vehicle_make', e.target.value)}
                        data-testid="tradein-make-input"
                      />
                      {errors.vehicle_make && (
                        <span className="field-error" role="alert">
                          <AlertCircle size={14} /> {errors.vehicle_make}
                        </span>
                      )}
                    </div>
                    <div className="form-group">
                      <label className="form-label">Model *</label>
                      <input
                        type="text"
                        className={`form-input ${errors.vehicle_model ? 'input-error' : ''}`}
                        placeholder="Camry"
                        value={formData.vehicle_model}
                        onChange={(e) => updateField('vehicle_model', e.target.value)}
                        data-testid="tradein-model-input"
                      />
                      {errors.vehicle_model && (
                        <span className="field-error" role="alert">
                          <AlertCircle size={14} /> {errors.vehicle_model}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">
                        <Gauge size={14} /> Mileage (km) *
                      </label>
                      <input
                        type="number"
                        className={`form-input ${errors.mileage ? 'input-error' : ''}`}
                        placeholder="85000"
                        value={formData.mileage}
                        onChange={(e) => updateField('mileage', e.target.value)}
                        data-testid="tradein-mileage-input"
                        min="0"
                      />
                      {errors.mileage && (
                        <span className="field-error" role="alert">
                          <AlertCircle size={14} /> {errors.mileage}
                        </span>
                      )}
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        <DollarSign size={14} /> Balance Owing (Optional)
                      </label>
                      <input
                        type="number"
                        className="form-input"
                        placeholder="0"
                        value={formData.balance_owing}
                        onChange={(e) => updateField('balance_owing', e.target.value)}
                        data-testid="tradein-balance-input"
                        min="0"
                        step="0.01"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Condition *</label>
                    <select
                      className={`form-input form-select ${errors.condition ? 'input-error' : ''}`}
                      value={formData.condition}
                      onChange={(e) => updateField('condition', e.target.value)}
                      data-testid="tradein-condition-select"
                    >
                      <option value="">Select Condition</option>
                      {VEHICLE_CONDITIONS.map(cond => (
                        <option key={cond} value={cond}>{cond}</option>
                      ))}
                    </select>
                    {errors.condition && (
                      <span className="field-error" role="alert">
                        <AlertCircle size={14} /> {errors.condition}
                      </span>
                    )}
                  </div>
                </div>

                {/* What You're Looking For */}
                <div className="form-section">
                  <h2>What You're Looking For</h2>
                  
                  <div className="form-group">
                    <label className="form-label">
                      <Car size={14} /> Vehicle Preference (Optional)
                    </label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="e.g., SUV, Truck, newer model with low mileage"
                      value={formData.vehicle_preference}
                      onChange={(e) => updateField('vehicle_preference', e.target.value)}
                      data-testid="tradein-preference-input"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      <FileText size={14} /> Additional Notes (Optional)
                    </label>
                    <textarea
                      className="form-input"
                      placeholder="Any other details about your vehicle or what you're looking for..."
                      value={formData.notes}
                      onChange={(e) => updateField('notes', e.target.value)}
                      data-testid="tradein-notes-input"
                      rows={3}
                      style={{ resize: 'vertical', minHeight: '80px' }}
                    />
                  </div>
                </div>

                {/* Consent */}
                <div className="form-section consent-section">
                  <div className={`consent-item ${errors.contact_consent ? 'consent-error' : ''}`}>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.contact_consent}
                        onChange={(e) => updateField('contact_consent', e.target.checked)}
                        data-testid="tradein-consent-checkbox"
                      />
                      <span className="checkmark"></span>
                      <span className="consent-text">
                        I agree to be contacted by Moto Money Finance regarding this trade-in enquiry and financing options. *
                      </span>
                    </label>
                    {errors.contact_consent && (
                      <span className="field-error" role="alert">
                        <AlertCircle size={14} /> {errors.contact_consent}
                      </span>
                    )}
                  </div>

                  <p className="privacy-note">
                    By submitting this form, you agree to our{" "}
                    <Link to="/privacy">Privacy Policy</Link>. 
                    This is an enquiry only and does not guarantee any specific trade-in value or financing approval.
                  </p>
                </div>

                {/* Honeypot */}
                <div className="hp-field" aria-hidden="true">
                  <input
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={formData.website}
                    onChange={(e) => updateField('website', e.target.value)}
                  />
                </div>

                <button 
                  type="submit" 
                  className="btn btn-primary submit-btn"
                  disabled={isSubmitting}
                  data-testid="submit-tradein-btn"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      Submit Trade-In Enquiry
                      <Check size={18} />
                    </>
                  )}
                </button>
              </form>
            </>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="site-footer" style={{ marginTop: '0' }}>
        <div className="footer-bottom">
          <p>&copy; {new Date().getFullYear()} Moto Money Finance. All rights reserved.</p>
          <p>
            <Link to="/privacy">Privacy Policy</Link>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default TradeIn;
